import React, { JSX, useContext, useEffect, useState } from 'react'
import { toJS } from 'mobx'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import SearchPanel from '@Components/Search/SearchPanel'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import WorkResultsCardTable from '@Components/WorkItemV2/WorkResultsCardTable'
import { PaginationBody } from '@Entities/pagination'
import { WorkModel } from '@Entities/work/work'
import { Works } from '@Entities/work/works'
import CatalogExtendedWorkItem from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem'
import { WorkSearch } from '@App/stores/search/models/work-search'

export const PUBLIC_WORK_SEARCH_QUERY_KEY = 'public-work-search'

const cardResultsDivClassName = 'public-search-results-table'

interface PublicSearchByWorkTabProps {
    bulkActionControlSection?: JSX.Element
    resultsOverride?: JSX.Element
    hideSearch?: boolean
}

const PublicSearchByWorkTab = (props: PublicSearchByWorkTabProps) => {
    const { bulkActionControlSection, resultsOverride, hideSearch } = props
    const { publicSearchWorksStore, publicViewWorkStore } = useContext(StoresContext)
    const queryClient = useQueryClient()

    // A link can carry search terms (?combinedTitles=…). They must be in the store before the form first
    // renders — it picks its rows up then — so this runs in a state initializer, not an effect.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        publicSearchWorksStore.searchCriteriaStore.criteriaOptions
    )
    useState(() => {
        if (urlCriteria) {
            publicSearchWorksStore.seedSearchFromUrl(urlCriteria)
        }
    })

    const query = toJS(publicSearchWorksStore.publicSearchByWorkSearchQuery)
    const pagination: PaginationBody = {
        page: publicSearchWorksStore.currentPage,
        limit: publicSearchWorksStore.currentResultsPerPageLimit,
    }

    const worksQuery = useSearchQuery<Works>({
        queryKey: [PUBLIC_WORK_SEARCH_QUERY_KEY, query, pagination],
        queryFn: async () => publicSearchWorksStore.fetchWorks(query, pagination),
        // Copied into the store from the query's data, not from the fetch: a cache hit never runs the fetch.
        applyData: (response) => publicSearchWorksStore.applySearchResults(response, pagination),
        initialData: publicSearchWorksStore.publicSearchByWorkCriteriaWorks,
        // Reset (and a fresh visit) leaves the query empty; a blank search should not hit the API on its own.
        enabled: Object.keys(query).length > 0,
        onError: (error) => publicSearchWorksStore.reportSearchError(error),
    })

    const handleSearch = (submittedSearchQuery: WorkSearch) => {
        // Clear immediately so a new search never shows the previous criteria's results, whether the
        // new fetch succeeds, fails, or (resubmitting identical criteria below) doesn't fire at all.
        syncCriteriaToUrl(submittedSearchQuery)
        publicSearchWorksStore.resetSearchResults()
        // Resubmitting identical criteria on page 1 leaves the query key unchanged, so a key-driven
        // refetch alone wouldn't fire — e.g. retrying after a failed search with the same criteria.
        const keyWillBeUnchanged = isEqual(query, submittedSearchQuery) && pagination.page === 1

        publicSearchWorksStore.setPublicSearchByWorkQuery(submittedSearchQuery)
        publicSearchWorksStore.setCurrentPage(1)

        if (keyWillBeUnchanged) {
            queryClient.invalidateQueries({ queryKey: [PUBLIC_WORK_SEARCH_QUERY_KEY] })
        }
    }

    const reset = () => {
        syncCriteriaToUrl(undefined)
        publicSearchWorksStore.resetPublicSearchByWorkCriteriaIfNeeded()
    }

    const handleClickCard = (selectedWork: WorkModel) => {
        publicViewWorkStore.setViewWorkPublic(selectedWork)
        publicSearchWorksStore.setRedirectingToCatalog(true)
    }

    useEffect(
        () => () => {
            publicSearchWorksStore.resetPublicSearchByWorkCriteriaIfNeeded()
        },
        []
    )

    return (
        <div className="public-search-by-work-tab">
            {!hideSearch && (
                <SearchPanel
                    searchCriteriaStore={publicSearchWorksStore.searchCriteriaStore}
                    onSearch={handleSearch}
                    canAddCriteria
                    onReset={reset}
                    disabled={worksQuery.isFetching}
                />
            )}
            {resultsOverride ?? (
                <WorkResultsCardTable
                    works={publicSearchWorksStore.publicSearchByWorkCriteriaWorks}
                    paginationInfo={publicSearchWorksStore.publicSearchByWorkCriteriaPaginationInfo}
                    limit={publicSearchWorksStore.currentResultsPerPageLimit}
                    loading={worksQuery.isFetching}
                    extendedWorkComponentType={CatalogExtendedWorkItem}
                    handleOnClick={handleClickCard}
                    displayType="public"
                    onChangePage={(newPagination) => publicSearchWorksStore.setCurrentPage(newPagination.page ?? 1)}
                    onChangeLimit={(newLimit) => publicSearchWorksStore.onChangeLimit(newLimit)}
                    outerDivClassName={cardResultsDivClassName}
                    bulkActionControlSection={bulkActionControlSection}
                />
            )}
        </div>
    )
}

export default observer(PublicSearchByWorkTab)
